import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, ArrowRight, CheckCircle2, FileText, Sparkles, ShieldCheck, Clock, Camera,
} from "lucide-react";

const STATUS_STEPS = ["Intake", "Review", "Filed", "Complete"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [progress, setProgress] = useState({ completed_steps: [], total: 0 });
  const [intake, setIntake] = useState(null);

  useEffect(() => {
    api.get("/tiers").then(({ data }) => setTiers(data)).catch(() => {});
    api.get("/progress").then(({ data }) => setProgress(data)).catch(() => {});
    api.get("/intake/me").then(({ data }) => setIntake(data && data.case_id ? data : null)).catch(() => {});
  }, []);

  const owned = user?.purchased_tiers || [];
  const hasFullService = owned.includes("full_service");
  const pct = progress.total ? Math.round((progress.completed_steps.length / progress.total) * 100) : 0;
  const ownedTiers = tiers.filter((t) => owned.includes(t.id));
  const currentStatusIdx = intake ? STATUS_STEPS.indexOf(intake.status) : -1;

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <h1 className="font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]" data-testid="dashboard-greeting">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="mt-2 text-[#595D5A]">Here's where you are on your S-Corp conversion.</p>

        {/* Plans owned */}
        <div className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Your plan</h2>
          {ownedTiers.length === 0 ? (
            <div className="mt-3 bg-white border border-[#E5E5E5] rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="no-plan-card">
              <div>
                <p className="font-medium text-[#1C1C1C]">You haven't chosen a plan yet</p>
                <p className="text-sm text-[#595D5A]">Pick a tier to unlock guided help or full-service filing.</p>
              </div>
              <Button
                onClick={() => navigate("/#pricing")}
                className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white"
                data-testid="choose-plan-btn"
              >
                View plans <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-3" data-testid="owned-tiers">
              {ownedTiers.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-2 bg-[#E6F0EB] text-[#114A31] font-medium px-4 py-2 rounded-full text-sm"
                >
                  <CheckCircle2 className="h-4 w-4" /> {t.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Control grid */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Self-guided progress */}
          <div className="md:col-span-2 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="progress-widget">
            <div className="flex items-center gap-2 text-[#114A31]">
              <BookOpen className="h-5 w-5" />
              <span className="font-heading font-semibold">Self-Guided Progress</span>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="font-heading text-3xl font-bold text-[#1C1C1C]">{pct}%</span>
              <span className="text-sm text-[#595D5A]">
                {progress.completed_steps.length}/{progress.total} steps
              </span>
            </div>
            <Progress value={pct} className="mt-3 h-2 bg-[#E6F0EB]" />
            <Button
              onClick={() => navigate("/guide")}
              variant="outline"
              className="mt-5 rounded-full border-[#E5E5E5] hover:border-[#114A31] hover:bg-[#F9F9F8] text-[#1C1C1C]"
              data-testid="open-guide-btn"
            >
              Continue the guide <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Savings card */}
          <div className="bg-[#114A31] text-white rounded-2xl p-6" data-testid="savings-widget">
            <Sparkles className="h-6 w-6 text-[#7FE3AE]" />
            <p className="mt-4 text-sm text-white/70">Potential annual savings</p>
            <p className="font-heading text-3xl font-bold">$8,400</p>
            <p className="mt-2 text-xs text-white/60">Estimate based on a $120k net profit</p>
          </div>
        </div>

        {/* Free Document Vault */}
        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="vault-widget">
          <div>
            <div className="flex items-center gap-2 text-[#114A31]">
              <Camera className="h-5 w-5" />
              <span className="font-heading font-semibold">Document Vault</span>
              <span className="text-[10px] font-bold tracking-wide uppercase bg-[#E6F0EB] text-[#114A31] px-2 py-0.5 rounded-full">Free</span>
            </div>
            <p className="mt-1 text-sm text-[#595D5A]">Snap receipts and tax docs from your phone — AI reads the vendor, date and amount and keeps a running expense summary.</p>
          </div>
          <Button onClick={() => navigate("/vault")} className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white shrink-0" data-testid="open-vault-btn">
            Open vault <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Documents & intake — available to all clients */}
        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="documents-widget">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-[#114A31]">
                <ShieldCheck className="h-5 w-5" />
                <span className="font-heading font-semibold">
                  {hasFullService ? "Full-Service Filing" : "Your Documents & Tax Forms"}
                </span>
              </div>
              <p className="mt-1 text-sm text-[#595D5A]">
                {hasFullService
                  ? "Complete your intake and upload documents so our specialists can file for you."
                  : "Upload your tax forms and documents securely so we can help with your S-Corp and taxes."}
              </p>
            </div>
            <Button
              onClick={() => navigate("/intake")}
              className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white"
              data-testid="open-intake-btn"
            >
              {intake ? "Update intake & documents" : "Start your intake"} <FileText className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {intake && (
            <div className="mt-6">
              <div className="flex items-center justify-between">
                {STATUS_STEPS.map((s, i) => (
                  <div key={s} className="flex-1 flex flex-col items-center text-center">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                        i <= currentStatusIdx ? "bg-[#114A31] text-white" : "bg-[#E6F0EB] text-[#114A31]"
                      }`}
                    >
                      {i < currentStatusIdx ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
                    </div>
                    <span className="mt-2 text-xs text-[#595D5A]">{s}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-[#595D5A] flex items-center gap-2">
                <Clock className="h-4 w-4" /> Current status:{" "}
                <span className="font-medium text-[#114A31]">{intake.status}</span> · {intake.files?.length || 0} document(s) uploaded
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
